import React from "react";
import { useModuleBEM, useUiStyles } from "@b2bportal/core-themes";
import {
  CoreUiComponents,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import clsx from "clsx";
import type {
  GetTypographyDefaultComponentFromConfig,
  GetTypographyDefaultTypeFromConfig,
  GetTypographySupportedTypesFromConfig,
  GetTypographySupportedVariationsFromConfig,
  TypographyGenericConfig,
} from "./types";
import {
  getTypographyDefaultComponentFromConfig,
  getTypographyDefaultType,
  getTypographyDefaultVariation,
  typographyDefaultConfig,
} from "./types";
import defaultStyles from "./Typography.module.scss";

type TypographyOwnProps<
  Config extends TypographyGenericConfig,
  Type extends GetTypographySupportedTypesFromConfig<Config>,
  Size extends GetTypographySupportedVariationsFromConfig<Config, Type>["size"]
> = PlatformComponentProps & {
  type?: Type;
  size?: Size;
} & Partial<
    Pick<
      GetTypographySupportedVariationsFromConfig<Config, Type>,
      "variant" | "weight"
    >
  >;

interface ComponentOverrideProps<Component extends React.ElementType> {
  component?: Component;
  ref?: React.LegacyRef<React.ElementRef<Component>>;
}

interface TypographyProps<
  Config extends TypographyGenericConfig,
  Type extends GetTypographySupportedTypesFromConfig<Config>,
  Size extends GetTypographySupportedVariationsFromConfig<Config, Type>["size"],
  Component extends React.ElementType = GetTypographyDefaultComponentFromConfig<
    Config,
    Type,
    Size
  >
> extends TypographyOwnProps<Config, Type, Size>,
    ComponentOverrideProps<Component>,
    PlatformComponentProps {}

type CombineProps<OwnProps, ComponentProps> = OwnProps &
  Omit<ComponentProps, keyof OwnProps>;

const COMPONENT_KEY = CoreUiComponents.Typography;

interface TypographyComponent<Config extends TypographyGenericConfig> {
  <
    Type extends GetTypographySupportedTypesFromConfig<Config> = GetTypographyDefaultTypeFromConfig<Config>,
    // Default size to `never` to use the `default` tag.
    Size extends GetTypographySupportedVariationsFromConfig<
      Config,
      Type
    >["size"] = never,
    Component extends React.ElementType = GetTypographyDefaultComponentFromConfig<
      Config,
      Type,
      Size
    >
  >(
    props: CombineProps<
      TypographyProps<Config, Type, Size, Component>,
      React.ComponentPropsWithoutRef<Component>
    >
  ): JSX.Element;
}

export const typographyForConfig = <Config extends TypographyGenericConfig>(
  config: Config
) =>
  React.forwardRef(
    <
      Type extends GetTypographySupportedTypesFromConfig<Config> = GetTypographyDefaultTypeFromConfig<Config>,
      // Default size to `never` to use the `default` tag.
      Size extends GetTypographySupportedVariationsFromConfig<
        Config,
        Type
      >["size"] = never,
      Component extends React.ElementType = GetTypographyDefaultComponentFromConfig<
        Config,
        Type,
        Size
      >
    >(
      props: CombineProps<
        TypographyProps<Config, Type, Size, Component>,
        React.ComponentPropsWithoutRef<Component>
      >,
      ref: React.ForwardedRef<React.ElementRef<Component>>
    ) => {
      const {
        className,
        children,
        type = getTypographyDefaultType(config),
        // Allow this to be undefined, define it later, so that we
        // can properly look up "unspecified" to get the default tag.
        size: sizeProp,
        component: Component = getTypographyDefaultComponentFromConfig(
          config,
          type,
          sizeProp
        ),
        ...restProps
      } = props;

      const variation = getTypographyDefaultVariation(type, config);
      const {
        variant = variation.variant,
        weight = variation.weight,
        ...componentProps
      } = restProps;

      const size = sizeProp ?? variation.size;

      const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
      const [block] = useModuleBEM(styles, COMPONENT_KEY, [
        type,
        size,
        `variant-${variant}`,
        `weight-${weight}`,
      ]);

      return (
        <Component
          {...componentProps}
          ref={ref}
          className={clsx(block, className)}
        >
          {children}
        </Component>
      );
    }
  ) as TypographyComponent<Config>;

export const Typography = typographyForConfig(typographyDefaultConfig);
